import { FC, useEffect, useRef, useMemo } from 'react';

import * as Yup from 'yup'
import {useFormik} from 'formik'
import { useNavigate } from 'react-router-dom';
import { StatusErrorMessage, FileUploader, FileUploaderResponse, InspectorAgreeCheck, FormikNotesComponent } from '../../../../../components/widgets';
import { doFormSubmit, processNoteValues, reloadDraftVars } from '../../../../../components/helpers';
import { PhaseFormObserver } from '../../components/PhaseFormObserver';
import { pickBy } from 'lodash';
import { Application } from '../../../../core/applications';
import { SubmitButtonComponent } from '../../../../../components/layout';

const issueReviewSchema: any = Yup.object({
  approved: Yup.boolean(),
  testDate: Yup.string().trim().label('Test Date'),
  testReportReference: Yup.string().trim().label('Surveillance Report Reference'),
  note: Yup.string().trim().label('Notes')
  .when("approved", {
    is: false,
    then: (schema) => schema.required('Details required.').label('Notes'),
  }),
  noteAttachments: Yup.array(),
  attachmentId: Yup.number().integer().label('Completed Audit Kit')
  .when(['approved', 'hasKit'], {
    is: (approved: boolean, hasKit : boolean) => approved && hasKit,
    then: (schema) => schema.integer().moreThan(0, 'Completed Audit Kit required').required().label('Completed Audit Kit'),
  }),
  surveillanceAttachmentId: Yup.number().integer().label('Surveillance Test Result')
  .when(['approved', 'hasSampling'], {
    is: (approved: boolean, hasSampling : boolean) => approved && hasSampling,
    then: (schema) => schema.integer().moreThan(0, 'Surveillance Test Result required').required().label('Surveillance Test Result'),
  }),
  cocAttachmentId: Yup.number().integer().label('Completed Draft COC')
  .when(['approved', 'hasCoC'], {
    is: (approved: boolean, hasCoC : boolean) => approved && hasCoC,
    then: (schema) => schema.integer().moreThan(0, 'Completed Draft COC required').required().label('Completed Draft COC'),
  }),
  inspectorAgree: Yup.bool().label('Confirmation')
  .when("approved", {
    is: true,
    then: (schema) => schema.oneOf([true], 'Must be checked'),
  }),
});

type Props = {
  appData: Application
  draftVars: Record<string, any>
  verdict: string
  doClose: Function  
}

const IssueReviewForm : FC<Props> = ({ appData, draftVars, verdict, doClose}) => {

  const navigate = useNavigate();

  const majorNCRef = useRef<HTMLInputElement>(null);

  const {appID, initValues, hasKit, hasSampling, hasCoC} = useMemo(() => {

    let appID = 0;
    let hasSampling = false;
    let hasKit = false;
    let hasCoC = false;

    let initValues = {
      approved: false,
      attachmentId: 0,
      attachmentName: '',
      surveillanceAttachmentId: 0,
      surveillanceAttachmentName: '',
      cocAttachmentId: 0,
      cocAttachmentName: '',
      majorNCFound: false,
      note: '',
      noteAttachments: [],
      testDate: '',
      testReportReference: '',
      hasSampling,
      hasKit,
      hasCoC, 
      inspectorAgree: false,
    };

    if (! appData) return {appID, initValues, hasKit, hasSampling, hasCoC};

    appID = (appData?.id) ? appData.id : 0;
    hasKit = (appData?.inspectionKit && appData.inspectionKit?.id) ? true : false;
    hasSampling = (appData?.samplingForm && appData.samplingForm?.id) ? true : false;
    hasCoC = (appData?.inspectionCOC && appData.inspectionCOC?.id) ? true : false;

    Object.assign(initValues, {appID, hasKit, hasSampling, hasCoC});

    reloadDraftVars(initValues, draftVars, ['applicationid','approved']);
    
    return {appID, initValues, hasKit, hasSampling, hasCoC};

  }, [appData, draftVars]);
  

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: issueReviewSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async (values, {setStatus, setSubmitting}) => {

      let approved = (verdict === 'approved') ? true : false;
      let payload = {
        ...pickBy(values, (value, key) => (
          value &&
          (! (key.match(/attachmentName$/i) || (key.match(/^has/i)) || key === 'inspectorAgree'))
        )),
        applicationid : appID,
        approved
      };
            
      if (approved) {
        const majorNCFound = (majorNCRef.current && majorNCRef.current.checked) ? true : false;
        if (majorNCFound && (! window.confirm("Major Non-Conformity Found! Do you still want to proceed?"))) {
          setSubmitting(false);
          return;
        }
        Object.assign(payload, { majorNCFound });
      } else {
        Object.assign(values, { majorNCFound : true});
      }

      processNoteValues(values, payload);
      
      await doFormSubmit('Phase_5_4Servlet', payload, setStatus, setSubmitting, () => {
        navigate('/setsco/application-management/applications');
      });            
    }
  })

  const {
    attachmentName,
    surveillanceAttachmentName,
    cocAttachmentName,
    majorNCFound, 
    approved,
  } = formik.values;

  const { validateForm } = formik;

  const doCancel = () => {
    doClose();
  }

  useEffect(() => {
    formik.setFieldValue('approved', (verdict === 'approved' ? true : false) );
    if (verdict !== 'approved') {
      formik.setFieldValue('majorNCFound', true);
    }
    if (verdict === 'approved') {
      formik.setFieldValue('note', '');
    } else {
      formik.setFieldValue('majorNCFound', true);
      if (majorNCRef.current) majorNCRef.current.checked = true;
    }
    // force revalidate notes field
    setTimeout(() => formik.validateForm(), 100);
    // prevent 'useEffect has missing dependency: formik' error with next line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verdict]);

  // set MajorNCFound checked
  useEffect(() => {
    if (initValues?.majorNCFound && majorNCRef.current) {
      majorNCRef.current.checked = initValues?.majorNCFound;
    }
  }, [initValues])

  useEffect(() => {
    validateForm();
  }, [approved, validateForm]);


  return (
  <>
  <PhaseFormObserver appID={appID} values={formik.values} isSubmitting={formik.isSubmitting} verdict={verdict} />
  <form id='kt_issue_review_form' className='form' onSubmit={formik.handleSubmit} noValidate>    
    <div
      className='d-flex flex-column gap-4 mb-6 px-2'
      id='kt_issue_review_scroll'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-max-height='auto'
      data-kt-scroll-dependencies='#kt_issue_review_header'
      data-kt-scroll-wrappers='#kt_issue_review_scroll'
      data-kt-scroll-offset='300px'
    >
      <div className='fv-row d-flex flex-row gap-4'>
        { verdict === 'approved' &&
        <div className="flex-grow-1 min-w-200px">
          <label className='form-label fw-bold'>Major Non-Conformity Found</label>
          <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid mt-2'>
            <input
              {...formik.getFieldProps('majorNCFound')}
              ref={majorNCRef}
              className='form-check-input'
              type='checkbox'
              onChange={() => {
                formik.setFieldValue('majorNCFound', (majorNCRef.current && majorNCRef.current.checked));
              }}
            />
            <label className='form-check-label'>{ majorNCFound ? 'YES' : 'NO'}</label>
          </div>
        </div>
        }
        <div className="flex-grow-1">
          <label className='fw-bold fs-6 mb-2'>Date of Surveillance Test</label>
          <textarea
            placeholder=''
            {...formik.getFieldProps('testDate')}
            name='testDate'
            className='form-control form-control-solid'
            rows={1}
          ></textarea>
          {formik.touched.testDate && formik.errors.testDate && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.testDate}</span>
            </div>
          </div>
          )}
        </div>
      </div>
      
      <div className="fv-row">
        <label className='fw-bold fs-6 mb-2'>Surveillance Report Reference</label>
        <textarea
          placeholder=''
          {...formik.getFieldProps('testReportReference')}
          name='testReportReference'
          className='form-control form-control-solid'
          rows={1}
        ></textarea>
        {formik.touched.testReportReference && formik.errors.testReportReference && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{formik.errors.testReportReference}</span>
          </div>
        </div>
        )}
      </div>

      <div className="fv-row">
        <FileUploader
          required={verdict === 'approved' && hasKit}
          mainTitle="Completed Audit Kit"
          subTitle={ attachmentName ? attachmentName : "Click or drop here to upload."}
          fileName={ attachmentName }
          allowMultiple={false}
          acceptFiles="UserFileFormats"
          onFileUploaded={ (dat: FileUploaderResponse) => {
            formik.setFieldValue("attachmentId", dat.id, false);
            formik.setFieldValue("attachmentName", dat.name, false);

            setTimeout(() => formik.validateField("attachmentId"), 100)
          }}
          onClearFile={ () => {
            formik.setFieldValue("attachmentId", 0, false);
            formik.setFieldValue("attachmentName", '', false);
            setTimeout(() => formik.validateField("attachmentId"), 100)
          }}
        />
        {formik.touched.attachmentId && formik.errors.attachmentId && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{formik.errors.attachmentId}</span>
          </div>
        </div>
        )}
      </div>

      <div className="fv-row">
        <FileUploader
          required={verdict === 'approved' && hasSampling}
          mainTitle="Surveillance Test Result"
          subTitle={ surveillanceAttachmentName ? surveillanceAttachmentName : "Click or drop here to upload."}
          fileName={ surveillanceAttachmentName }
          allowMultiple={false}
          acceptFiles="UserFileFormats"
          onFileUploaded={ (dat: FileUploaderResponse) => {
            formik.setFieldValue("surveillanceAttachmentId", dat.id, false);
            formik.setFieldValue("surveillanceAttachmentName", dat.name, false);

            setTimeout(() => formik.validateField("surveillanceAttachmentId"), 100)
          }}
          onClearFile={ () => {
            formik.setFieldValue("surveillanceAttachmentId", 0, false);
            formik.setFieldValue("surveillanceAttachmentName", '', false);
            setTimeout(() => formik.validateField("surveillanceAttachmentId"), 100)
          }}
        />
        {formik.touched.surveillanceAttachmentId && formik.errors.surveillanceAttachmentId && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{formik.errors.surveillanceAttachmentId}</span>
          </div>
        </div>
        )}
      </div>

      <div className="fv-row">
        <FileUploader
          required={verdict === 'approved' && hasCoC}
          mainTitle="Completed Draft COC"
          subTitle={ cocAttachmentName ? cocAttachmentName : "Click or drop here to upload."}
          fileName={ cocAttachmentName }
          allowMultiple={false}
          acceptFiles="UserFileFormats"
          onFileUploaded={ (dat: FileUploaderResponse) => {
            formik.setFieldValue("cocAttachmentId", dat.id, false);
            formik.setFieldValue("cocAttachmentName", dat.name, false);

            setTimeout(() => formik.validateField("cocAttachmentId"), 100)
          }}
          onClearFile={ () => {
            formik.setFieldValue("cocAttachmentId", 0, false);
            formik.setFieldValue("cocAttachmentName", '', false);
            setTimeout(() => formik.validateField("cocAttachmentId"), 100)
          }}
        />
        {formik.touched.cocAttachmentId && formik.errors.cocAttachmentId && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{formik.errors.cocAttachmentId}</span>
          </div>
        </div>
        )}
      </div>

      <FormikNotesComponent formik={formik} label={ verdict === 'rejected' ? 'Reason' : "Notes"} required={verdict === 'rejected'} />

      { verdict === 'approved' &&
      <InspectorAgreeCheck formik={formik} />
      }
      <StatusErrorMessage status={formik.status} />
      <SubmitButtonComponent 
        formik={formik}
        onCancel={doCancel}
      />
    </div>
  </form>
  </>
  )
}


export {IssueReviewForm}